import { Injectable } from '@angular/core';
import { PrintCardContentViewModel } from '../../shared/print-card-content-view-model';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { exists } from '../../../../../../../functions/exists';
import { ColorUtils } from '../../../../../../../utils/color-utils';
import { SectionColumnConfigDataForStrainTypeColumn } from '../../../../../../../models/enum/shared/section-column-config-data-value';
import { StrainType } from '../../../../../../../models/enum/dto/strain-classification.enum';
import { BadgeUtils } from '../../../../../../../utils/badge-utils';
import { SectionLayoutType } from '../../../../../../../models/enum/dto/section-layout-type.enum';
import { FilterUtils } from '../../../../../../../utils/filter-utils';
import { CannabinoidOrTerpeneFromPool, PropertyPoolSorter } from '../../shared/card-data-with-property-pooling';
import { Cannabinoid, PrimaryCannabinoid } from '../../../../../../../models/enum/shared/cannabinoid';
import { SpecificPriceWithoutDiscounts } from '../../../../../../../models/enum/shared/specific-price-without-discounts.enum';
import { SortUtils } from '../../../../../../../utils/sort-utils';
import type { CardData } from '../../../../../../../models/print-cards/card-data';
import { PrintCardProperty } from '../../../../../../../utils/print-card-property-definition';
import type { Terpene } from '../../../../../../../models/enum/shared/terpene';

@Injectable()
export abstract class PlantlifeBusinessCardStackSharedLogicContentViewModel extends PrintCardContentViewModel {
  // Pin THC and CBD to front, but same group value so higher
  // CBD values are placed before THC in the final sort order
  private readonly priorityPropertyGroupMap = new Map<string, number>([
    [PrimaryCannabinoid.THC, 0],
    [PrimaryCannabinoid.CBD, 0],
    [this.totalTerpenesPropertyTitle(), 1]
  ]);

  private readonly _badgesInlineWithTitleHeight = new BehaviorSubject<number>(13);
  public readonly badgesInlineWithTitleHeight$ = this._badgesInlineWithTitleHeight as Observable<number>;
  connectToBadgesInlineWithTitleHeight = (height: number) => {
    this._badgesInlineWithTitleHeight.next(height);
  };

  public readonly topOfCardBackgroundColor$ = this.cardAccentColor$.pipe(map(accentColor => accentColor || '#000000'));

  public readonly topOfCardTextColor$ = combineLatest([this.headerTextColor$, this.topOfCardBackgroundColor$]).pipe(
    map(([headerTextColor, accentColor]) => {
      if (exists(headerTextColor)) return headerTextColor;
      return ColorUtils.isDarkColor(accentColor) ? '#76AB52' : '#000000';
    })
  );

  public readonly strainSymbol$ = this.cardData$.pipe(
    map(cardData => {
      return cardData?.getReadableStrainType(SectionColumnConfigDataForStrainTypeColumn.StrainTypeSymbol);
    })
  );

  public readonly strainDeterminedBackgroundColor$ = this.cardData$.pipe(
    map(cardData => {
      const type = cardData?.getStrainType();
      switch (type) {
        case StrainType.Indica:
        case StrainType.IndicaDominant:
          return '#7663E3';
        case StrainType.Sativa:
        case StrainType.SativaDominant:
          return '#F0A005';
        case StrainType.Hybrid:
          return '#77D11F';
        case StrainType.Blend:
          return '#5690F0';
        case StrainType.CBD:
          return '#AC1F49';
        default:
          return '#000000';
      }
    })
  );

  public readonly propertyContainerLeftPaddingInPx$ = this.propertiesBasedOnPools$.pipe(
    map(propertiesBasedOnPools => {
      switch (propertiesBasedOnPools?.length) {
        case 1:
          return 48;
        case 2:
          return 28;
        case 3:
          return 8;
        case 4:
          return 6;
        default:
          return 0;
      }
    })
  );

  public readonly maxWidthForPropertyScaleDownInPx$ = this.propertiesBasedOnPools$.pipe(
    map(propertiesBasedOnPools => {
      switch (propertiesBasedOnPools?.length) {
        case 1:
          return 172;
        case 2:
          return 68;
        default:
          return 40;
      }
    })
  );

  public readonly showSize$ = this.cardData$.pipe(
    map(cardData => {
      const size = cardData?.getSize();
      return exists(size) && size !== '0' && size !== '-';
    })
  );

  public readonly badgesInlineWithName$ = this.cardData$.pipe(
    map(cardData => {
      if (!cardData) return [];
      const badges = BadgeUtils.getBadges(SectionLayoutType.List, 0, cardData.variantBadgeMap, cardData.rowVariants);
      return badges?.filter(badge => FilterUtils.keepPlantLifeTaggedBadges(badge));
    })
  );

  public readonly hasBadgesInlineWithName$ = this.badgesInlineWithName$.pipe(map(badges => (badges?.length ?? 0) > 0));

  public readonly priceWithoutDiscounts$ = of([
    SpecificPriceWithoutDiscounts.LocationSpecificBasePrice,
    SpecificPriceWithoutDiscounts.CompanySpecificBasePrice,
    SpecificPriceWithoutDiscounts.BasePrice
  ]);

  /**
   * Desired property order THC > CBD > Total Terpenes > Secondary Cannabinoids
   * with caveat, existing behavior places higher CBD values before THC.
   */
  protected override finalSortLambdaForCompoundProperties(): PropertyPoolSorter {
    const defaultSort = this.getSecondaryPoolOfCompoundPropertiesSortLambda();
    return (a: CannabinoidOrTerpeneFromPool, b: CannabinoidOrTerpeneFromPool) => {
      return SortUtils.pinned(this.priorityPropertyGroupMap, a?.title, b?.title) || defaultSort(a, b);
    };
  }

  protected override getPrimaryPoolOfCompoundProperties(
    cardData: CardData,
    maxProps: number,
    visibleTotalTerpenes: CannabinoidOrTerpeneFromPool | null,
    visibleTAC: CannabinoidOrTerpeneFromPool | null,
    visibleTopTerp: CannabinoidOrTerpeneFromPool | null,
    visiblePresentTerps: CannabinoidOrTerpeneFromPool | null
  ): CannabinoidOrTerpeneFromPool[] {
    if (cardData?.isNonCannabinoidVariant() || maxProps <= 0) return [];
    const properties: CannabinoidOrTerpeneFromPool[] = [];
    this.addVisiblePrimaryCannabinoidToPrimaryPool(
      cardData,
      PrintCardProperty.THC,
      PrimaryCannabinoid.THC,
      maxProps,
      properties
    );
    this.addVisiblePrimaryCannabinoidToPrimaryPool(
      cardData,
      PrintCardProperty.CBD,
      PrimaryCannabinoid.CBD,
      maxProps,
      properties
    );
    this.addVisibleTotalTerpenesToPrimaryPool(visibleTotalTerpenes, maxProps, properties);
    this.addVisibleTACToPrimaryPool(visibleTAC, maxProps, properties);
    this.addVisibleTopTerpeneToPrimaryPool(visibleTopTerp, maxProps, properties);
    this.addVisiblePresentTerpsToPrimaryPool(visiblePresentTerps, maxProps, properties);
    return properties;
  }

  /**
   * Plantlife promotes admin-enabled THC and CBD into the priority pool so they can be ordered before the
   * remaining default pooling properties. Remove them from the default secondary candidates to avoid duplicates
   * while preserving the rest of the admin-aware secondary pool.
   */
  protected override whatPropertiesToUseForSecondaryPoolOfCompoundProperties(
    cardData: CardData
  ): (Cannabinoid | Terpene)[] {
    return super
      .whatPropertiesToUseForSecondaryPoolOfCompoundProperties(cardData)
      ?.filter(property => property !== PrimaryCannabinoid.THC && property !== PrimaryCannabinoid.CBD);
  }

  private addVisiblePrimaryCannabinoidToPrimaryPool(
    cardData: CardData,
    adminProperty: PrintCardProperty,
    cannabinoid: PrimaryCannabinoid,
    maxProps: number,
    properties: CannabinoidOrTerpeneFromPool[]
  ): void {
    if (maxProps - properties.length < 1 || !this.adminPropertyEnabled(cardData, adminProperty)) return;
    const value = cardData?.getCannabinoid(cannabinoid);
    if (this.propertyAsStringExists(value)) properties.push({ title: cannabinoid, value });
  }

  private adminPropertyEnabled(cardData: CardData, property: PrintCardProperty): boolean {
    const cardSize = cardData?.menu?.metadata?.printCardSize;
    return cardData?.menu?.hydratedTheme?.printConfig?.visiblePropertyMap.get(cardSize)?.includes(property) ?? false;
  }
}
